import { useEffect, useRef, useSyncExternalStore } from "react";
import { useLocation } from "wouter";
import { X } from "lucide-react";
import {
  dismissUpdateToast,
  getUpdateToastState,
  onClientNavigation,
  reloadForUpdate,
  subscribeUpdateToast,
} from "@/lib/appVersion";

/**
 * Bridges the appVersion runtime (client/src/lib/appVersion) and React:
 * reports wouter navigations (a pending update then becomes a full load of
 * the new URL) and renders the non-blocking "New version available" notice.
 *
 * Not the shadcn toaster on purpose: use-toast removes every toast after
 * 3 s and keeps only one, and this notice must stay until acted on.
 */
export function AppUpdateWatcher() {
  const [location] = useLocation();
  const isFirstLocation = useRef(true);
  useEffect(() => {
    if (isFirstLocation.current) {
      isFirstLocation.current = false; // mount, not a navigation
      return;
    }
    onClientNavigation();
  }, [location]);

  const { toastBuildId } = useSyncExternalStore(
    subscribeUpdateToast,
    getUpdateToastState,
    getUpdateToastState,
  );
  if (!toastBuildId) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="app-update-toast"
      data-build-id={toastBuildId}
      className="fixed bottom-4 left-4 z-[100] flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-md border bg-background p-4 pr-10 text-foreground shadow-lg"
    >
      <div className="grid gap-0.5">
        <p className="text-sm font-semibold">New version available</p>
        <p className="text-sm opacity-90">Reload to get the latest version of administr.</p>
      </div>
      <button
        type="button"
        data-testid="app-update-reload"
        onClick={reloadForUpdate}
        className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Reload
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="app-update-dismiss"
        onClick={dismissUpdateToast}
        className="absolute right-2 top-2 rounded-md p-1 text-foreground/50 hover:text-foreground focus:outline-none focus:ring-2"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
